// src/components/InstallPrompt.jsx
// PWA install prompt - ONLY shown to agents, agency admins, and founders
// NOT shown to clients

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

// Roles that can install the PWA
const ALLOWED_ROLES = ['agent', 'agency_admin', 'founder', 'fmo'];

const InstallPrompt = () => {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Don't do anything until user is logged in
    if (!user || !user.id) {
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user has an allowed role
    if (!ALLOWED_ROLES.includes(user.role)) {
      console.log('[PWA] Install prompt blocked - user role not allowed:', user.role);
      return;
    }

    // Check if user has already dismissed (stored in localStorage)
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      // Don't show again for 7 days after dismissal
      if (daysSinceDismissed < 7) {
        console.log('[PWA] Install prompt hidden - recently dismissed');
        return;
      }
    }

    // Check if there's already a deferred prompt from the global handler (index.js)
    const existingPrompt = window.getDeferredInstallPrompt?.();
    if (existingPrompt) {
      setDeferredPrompt(existingPrompt);
      setShowPrompt(true);
      console.log('[PWA] Install prompt ready for user (from global handler)');
    }

    // Listen for custom event from global handler (for prompts that arrive later)
    const handleInstallAvailable = (e) => {
      // Double-check role (in case user changed)
      if (!user || !ALLOWED_ROLES.includes(user.role)) {
        return;
      }

      setDeferredPrompt(e.detail);
      setShowPrompt(true);
      console.log('[PWA] Install prompt ready for user');
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('[PWA] App installed');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      window.clearDeferredInstallPrompt?.();
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [user]);

  // Don't render if not allowed or already installed
  if (!showPrompt || isInstalled || !user || !ALLOWED_ROLES.includes(user.role)) {
    return null;
  }

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice;
    console.log('[PWA] User choice:', outcome);

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }

    // Clear the prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    // Remember dismissal
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
    setShowPrompt(false);
    setDeferredPrompt(null);
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-50 animate-slide-up">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 bg-[#172A3A] rounded-lg flex items-center justify-center">
          <img src="/logo192.png" alt="Pro IRP" className="w-8 h-8" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm">Install Pro IRP</h3>
          <p className="text-xs text-gray-600 mt-0.5">
            Get instant notifications and quick access from your home screen
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-3">
        <button
          onClick={handleDismiss}
          className="flex-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Not now
        </button>
        <button
          onClick={handleInstall}
          className="flex-1 px-3 py-2 text-sm font-medium text-white bg-[#172A3A] hover:bg-[#1e3a4f] rounded-lg transition-colors"
        >
          Install
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
